import { useCallback, useLayoutEffect, useRef } from 'react';
import { useInteractOutside } from 'react-aria';
import type { RefObject } from 'react';

type UseNonModalPopoverDismissalArgs = {
    popoverRef: RefObject<Element | null>;
    triggerRef?: RefObject<Element | null>;
    isNonModal: boolean;
    isOpen: boolean;
    shouldCloseOnInteractOutside?: (element: Element) => boolean;
    containsDescendantElement: (element: Node) => boolean;
    close: () => void;
};

/**
 * Closes the popover when the user interacts outside of it, unless the target is a descendant of the popover or the interact outside callback returns false.
 */
export const useNonModalPopoverDismissal = ({
    popoverRef,
    triggerRef,
    isNonModal,
    isOpen,
    shouldCloseOnInteractOutside,
    containsDescendantElement,
    close,
}: UseNonModalPopoverDismissalArgs) => {
    const scheduledCloseTimeoutRef = useRef<number | null>(null);

    const clearScheduledClose = useCallback(() => {
        if (scheduledCloseTimeoutRef.current === null) return;

        window.clearTimeout(scheduledCloseTimeoutRef.current);
        scheduledCloseTimeoutRef.current = null;
    }, []);

    const maybeClose = useCallback(
        (target: EventTarget | null) => {
            const targetNode = target as Node | null;
            // Normalize text-node targets to Elements for React Aria's close veto callback.
            const targetElement =
                targetNode?.nodeType === Node.ELEMENT_NODE ? (targetNode as Element) : targetNode?.parentElement;

            if (!isNonModal || !isOpen) return;
            if (!targetNode?.nodeType || !targetElement) return;
            if (triggerRef?.current?.contains(targetNode)) return;
            if (popoverRef.current?.contains(targetNode)) return;
            if (containsDescendantElement(targetNode)) return;
            if (shouldCloseOnInteractOutside?.(targetElement) === false) return;
            if (scheduledCloseTimeoutRef.current !== null) return;

            // Defer closing so the outside target's own click handler can run before unmount.
            scheduledCloseTimeoutRef.current = window.setTimeout(() => {
                scheduledCloseTimeoutRef.current = null;
                close();
            }, 0);
        },
        [close, containsDescendantElement, isNonModal, isOpen, popoverRef, shouldCloseOnInteractOutside, triggerRef],
    );

    useInteractOutside({
        ref: popoverRef,
        isDisabled: !isNonModal || !isOpen,
        onInteractOutside: (event) => maybeClose(event.target),
    });

    useLayoutEffect(() => {
        if (isNonModal && isOpen) return;

        clearScheduledClose();
    }, [clearScheduledClose, isNonModal, isOpen]);

    useLayoutEffect(() => clearScheduledClose, [clearScheduledClose]);
};
